import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Stack,
  Typography,
  Autocomplete,
  Chip,
} from "@mui/material";
import {
  type PlotRequestTag,
  type AdditionalFieldsFormData,
} from "@/features/map/types";
import { useAdditionalFieldsConfig } from "@/features/map/hooks/useAdditionalFieldsConfig";
import { renderAdditionalFieldInput } from "@/features/map/utils/renderAdditionalFieldInput";

export interface PlotRequestFilterValues {
  createdAtFrom: string | null;
  createdAtTo: string | null;
  updatedAtFrom: string | null;
  updatedAtTo: string | null;
  tags: PlotRequestTag[];
  status: Array<"SUCCEED" | "FAILED">;
  type: Array<"土地" | "建物" | "区分建物">;
  requestAddress: string | null;
  requestLocation: string | null;
  requestChiban: string | null;
  requestKaokuNumber: string | null;
  remarks: string | null;
  additionalFields?: AdditionalFieldsFormData;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  availableTags: PlotRequestTag[];
  initialFilters?: PlotRequestFilterValues;
  onApply: (filters: PlotRequestFilterValues) => void;
}

export const MapPlotRequestFilterModal: React.FC<IProps> = ({
  open,
  onClose,
  availableTags,
  initialFilters,
  onApply,
}) => {
  const { availableFields, hasAvailableFields } = useAdditionalFieldsConfig();

  // 追加項目のデフォルト値を生成
  const additionalFieldsDefaultValues = React.useMemo(() => {
    if (!availableFields) return {};
    const defaults: AdditionalFieldsFormData = {};
    Object.keys(availableFields).forEach((key) => {
      defaults[key] = "";
    });
    return defaults;
  }, [availableFields]);

  const [filters, setFilters] = useState<PlotRequestFilterValues>({
    createdAtFrom: null,
    createdAtTo: null,
    updatedAtFrom: null,
    updatedAtTo: null,
    tags: [],
    status: [],
    type: [],
    requestAddress: null,
    requestLocation: null,
    requestChiban: null,
    requestKaokuNumber: null,
    remarks: null,
    additionalFields: additionalFieldsDefaultValues,
  });

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
    }
  }, [initialFilters, open]);

  const handleApply = (): void => {
    onApply(filters);
    onClose();
  };

  const handleClose = (
    _: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason === "backdropClick") {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">フィルター条件</Typography>
          <Button
            variant="outlined"
            onClick={onClose}
            size="small"
            sx={{
              minWidth: "auto",
            }}
          >
            × 閉じる
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ flex: 1, overflowY: "auto" }}>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {/* ステータス */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              ステータス
            </Typography>
            <Autocomplete
              multiple
              options={["SUCCEED", "FAILED"] as const}
              getOptionLabel={(option) =>
                option === "SUCCEED" ? "成功" : "失敗"
              }
              value={filters.status}
              onChange={(_, newValue) => {
                setFilters({
                  ...filters,
                  status: newValue as Array<"SUCCEED" | "FAILED">,
                });
              }}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} placeholder="ステータスを選択" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option === "SUCCEED" ? "成功" : "失敗"}
                    {...getTagProps({ index })}
                    key={option}
                    size="small"
                  />
                ))
              }
            />
          </Box>

          {/* 種別 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              種別
            </Typography>
            <Autocomplete
              multiple
              options={["土地", "建物", "区分建物"] as const}
              getOptionLabel={(option) => option}
              value={filters.type}
              onChange={(_, newValue) => {
                setFilters({
                  ...filters,
                  type: newValue,
                });
              }}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} placeholder="種別を選択" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const label = option;
                  return (
                    <Chip
                      label={label}
                      {...getTagProps({ index })}
                      key={option}
                      size="small"
                    />
                  );
                })
              }
            />
          </Box>

          {/* 住居表示 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              住居表示
            </Typography>
            <TextField
              placeholder="住居表示を入力"
              value={filters.requestAddress ?? ""}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  requestAddress: e.target.value || null,
                });
              }}
              fullWidth
            />
          </Box>

          {/* 所在・地番・家屋番号 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              所在・地番・家屋番号
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="所在"
                placeholder="所在を入力"
                value={filters.requestLocation ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    requestLocation: e.target.value || null,
                  });
                }}
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="地番"
                  placeholder="地番を入力"
                  value={filters.requestChiban ?? ""}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      requestChiban: e.target.value || null,
                    });
                  }}
                  fullWidth
                />
                <TextField
                  label="家屋番号"
                  placeholder="家屋番号を入力"
                  value={filters.requestKaokuNumber ?? ""}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      requestKaokuNumber: e.target.value || null,
                    });
                  }}
                  fullWidth
                />
              </Stack>
            </Stack>
          </Box>

          {/* タグ（複数選択可） */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              タグ（複数選択可）
            </Typography>
            <Autocomplete
              multiple
              options={availableTags}
              getOptionLabel={(option) => option.name}
              value={filters.tags}
              onChange={(_, newValue) => {
                setFilters({
                  ...filters,
                  tags: newValue,
                });
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} placeholder="タグを選択" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                  />
                ))
              }
            />
          </Box>

          {/* 備考 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              備考
            </Typography>
            <TextField
              placeholder="備考を入力"
              value={filters.remarks ?? ""}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  remarks: e.target.value || null,
                });
              }}
              fullWidth
            />
          </Box>

          {/* カスタム項目 */}
          {hasAvailableFields && availableFields?.fields && (
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mb: 1.5 }}
              >
                カスタム項目
              </Typography>
              <Stack spacing={2}>
                {availableFields.fields.map((field) => {
                  const fieldValue = filters.additionalFields?.[field.id] ?? "";

                  // booleanの場合は特別にAutocompleteを使用
                  if (field.fieldType === "boolean") {
                    return (
                      <Autocomplete
                        key={field.id}
                        options={["true", "false"]}
                        getOptionLabel={(option) =>
                          option === "true" ? "はい" : "いいえ"
                        }
                        value={fieldValue ?? null}
                        onChange={(_, newValue) => {
                          setFilters({
                            ...filters,
                            additionalFields: {
                              ...filters.additionalFields,
                              [field.id]: newValue ?? "",
                            },
                          });
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label={field.key} />
                        )}
                      />
                    );
                  }

                  // その他のフィールドタイプは共通関数を使用
                  return (
                    <Box key={field.id}>
                      {renderAdditionalFieldInput({
                        fieldType: field.fieldType,
                        value: fieldValue,
                        onChange: (newValue) => {
                          setFilters({
                            ...filters,
                            additionalFields: {
                              ...filters.additionalFields,
                              [field.id]: newValue,
                            },
                          });
                        },
                        label: field.key,
                        placeholder: `${field.key}を入力`,
                        fullWidth: true,
                      })}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* 作成日時 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              作成日時
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="以降"
                type="datetime-local"
                value={filters.createdAtFrom ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    createdAtFrom: e.target.value || null,
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
              <TextField
                label="以前"
                type="datetime-local"
                value={filters.createdAtTo ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    createdAtTo: e.target.value || null,
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </Stack>
          </Box>

          {/* 更新日時 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              更新日時
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="以降"
                type="datetime-local"
                value={filters.updatedAtFrom ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    updatedAtFrom: e.target.value || null,
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
              <TextField
                label="以前"
                type="datetime-local"
                value={filters.updatedAtTo ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    updatedAtTo: e.target.value || null,
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
        <Button
          onClick={handleApply}
          color="primary"
          variant="contained"
          size="large"
          sx={{ minWidth: 120 }}
        >
          絞り込む
        </Button>
      </DialogActions>
    </Dialog>
  );
};
